import React from 'react';
import styled, { keyframes } from 'styled-components';
import { FiChevronDown } from 'react-icons/fi';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Section = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
`;

const BgImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url('/images/barn-front.jpg');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;

  @media (max-width: 768px) {
    background-attachment: scroll;
  }
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    180deg,
    rgba(40, 20, 10, 0.5) 0%,
    rgba(40, 20, 10, 0.3) 40%,
    rgba(40, 20, 10, 0.6) 100%
  );
`;

const Content = styled.div`
  text-align: center;
  position: relative;
  z-index: 1;
  max-width: 900px;
  padding: 0 20px;
`;

const Title = styled.h1`
  font-size: clamp(4rem, 12vw, 8rem);
  color: var(--wheat);
  margin-bottom: 10px;
  line-height: 0.9;
  letter-spacing: -2px;
  text-shadow: 0 4px 30px rgba(0,0,0,0.3);
  animation: ${fadeUp} 0.8s ease both;
  animation-delay: 0.2s;
`;

const Subtitle = styled.div`
  font-family: 'Playfair Display', serif;
  font-size: clamp(1.3rem, 3.5vw, 2.2rem);
  color: var(--wheat);
  font-style: italic;
  margin-bottom: 50px;
  font-weight: 400;
  opacity: 0.9;
  animation: ${fadeUp} 0.8s ease both;
  animation-delay: 0.4s;
`;

const NavButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  flex-wrap: wrap;
  animation: ${fadeUp} 0.8s ease both;
  animation-delay: 0.6s;
`;

const NavButton = styled.div`
  padding: 14px 32px;
  background: rgba(139, 35, 35, 0.5);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(245, 222, 179, 0.3);
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.95rem;
  color: var(--wheat);
  transition: all 0.3s ease;
  cursor: pointer;
  letter-spacing: 0.5px;

  &:hover {
    background: rgba(139, 35, 35, 0.8);
    border-color: rgba(245, 222, 179, 0.6);
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(0,0,0,0.3);
  }

  @media (max-width: 600px) {
    padding: 12px 24px;
    font-size: 0.85rem;
  }
`;

const bounce = keyframes`
  0%, 100% { transform: translateX(-50%) translateY(0); }
  50% { transform: translateX(-50%) translateY(8px); }
`;

const ScrollIndicator = styled.div`
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: var(--wheat);
  cursor: pointer;
  animation: ${bounce} 2s ease-in-out infinite;
  opacity: 0.7;
  transition: opacity 0.3s ease;
  z-index: 1;

  &:hover {
    opacity: 1;
  }

  span {
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 3px;
    text-transform: uppercase;
  }
`;

export default function Hero() {
  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Section id="hero">
      <BgImage />
      <Overlay />

      <Content>
        <Title>Red Barn</Title>
        <Subtitle>Gardens</Subtitle>

        <NavButtons>
          <NavButton onClick={() => scrollTo('greenhouse')}>Greenhouse</NavButton>
          <NavButton onClick={() => scrollTo('events')}>Events</NavButton>
          <NavButton onClick={() => scrollTo('our-story')}>Our Story</NavButton>
          <NavButton onClick={() => scrollTo('contact')}>Contact Us</NavButton>
          <NavButton onClick={() => scrollTo('greeter')}>Meet Our Greeter</NavButton>
        </NavButtons>
      </Content>

      <ScrollIndicator onClick={() => scrollTo('greenhouse')}>
        <span>Explore</span>
        <FiChevronDown size={22} />
      </ScrollIndicator>
    </Section>
  );
}
